import React from "react";
import { useLocale, WithLocale } from "@insightfulscience/i18";

import PageMeta from "@insightfulscience/smart-react/PageMeta";
import BlockQuote, {
	BlockQuoteItem,
} from "@insightfulscience/atomic-react/r1/molecules/BlockQuote";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import ConvertFileFormatsSection, {
	ConvertFileFormatsHead,
	FileFormatsOptions,
	FileFormatsOptionsLinkWrapper,
	FileFormatsOptionsLink,
} from "@insightfulscience/atomic-react/r1/molecules/ConvertFileFormats";
import Section from "@insightfulscience/atomic-react/Section";
import { Col, Container, Row } from "@insightfulscience/atomic-react/Grid";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Html from "@insightfulscience/smart-react/Html";
import NextLink from "@insightfulscience/smart-react/NextLink";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SectionTryForFree from "@src/SectionTryForFree";
import SiteLayout from "@src/SiteLayout";
import resources from "../../locales/convert-file-formats";

const ConvertFileFormats = () => {
	const [t] = useLocale("convert-file-formats");
	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
			</Hero>
			<ConvertFileFormatsSection className="mb-20 mt-15">
				<ConvertFileFormatsHead title={t("SUPPORT_FILE_FORMATS_TITLE")}>
					{t("SUPPORT_FILE_FORMATS_SUBTITLE")}
				</ConvertFileFormatsHead>
				<FileFormatsOptions>
					<FileFormatsOptionsLinkWrapper md="auto">
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-alignment-formats-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_1")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-ape-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_2")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-clc-bio-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_3")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-clone-manager-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_4")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-dna-strider-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_5")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-dnadynamo-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_6")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-dnasis-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_7")}
						</NextLink>

						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-dnassist-files"
						>
							{t("SUPPORT_FILE_FIRST_COL_ITEM_8")}
						</NextLink>
					</FileFormatsOptionsLinkWrapper>
					<FileFormatsOptionsLinkWrapper md="auto">
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-dnastar-lasergene-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_1")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-ds-gene-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_2")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-embl-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_3")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-enzymex-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_4")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-genbank-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_5")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-gene-construction-kit-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_6")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-geneious-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_7")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-genetool-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_8")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-genome-compiler-files"
						>
							{t("SUPPORT_FILE_SECOND_COL_ITEM_9")}
						</NextLink>
					</FileFormatsOptionsLinkWrapper>
					<FileFormatsOptionsLinkWrapper md="shrink">
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-jellyfish-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_1")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-macvector-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_2")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-pdraw32-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_3")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-sequencher-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_4")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-serial-cloner-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_5")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-swiss-prot-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_6")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-vector-nti-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_7")}
						</NextLink>
						<NextLink
							Component={FileFormatsOptionsLink}
							href="/guides/convert-visual-cloning-files"
						>
							{t("SUPPORT_FILE_THIRD_COL_ITEM_8")}
						</NextLink>
					</FileFormatsOptionsLinkWrapper>
				</FileFormatsOptions>
			</ConvertFileFormatsSection>
			<Section spacing={{ my: 8 }}>
				<Container>
					<Row utility={{ justify: "center" }}>
						<Col md={8}>
							<Paragraph>{t("SUPPORT_FILE_TEXT_1")}</Paragraph>
						</Col>
						<Col md={8}>
							<Paragraph>
								<Html Tag="span">
									{t("SUPPORT_FILE_TEXT_2", {
										contactLink:
											"https://support.snapgene.com/hc/en-us/requests/new",
									})}
								</Html>
							</Paragraph>
						</Col>
					</Row>
				</Container>
			</Section>
			<BlockQuote>
				<BlockQuote.Head>
					<BlockQuoteItem
						active
						author={t("BACK_QUOTE_AUTHOR")}
						institution={t("BACK_QUOTE_INSTITUTION")}
					>
						{t("BACK_QUOTE_TEXT")}
					</BlockQuoteItem>
				</BlockQuote.Head>
			</BlockQuote>
			<ConvertFileFormatsSection className="mb-15">
				<ConvertFileFormatsHead title={t("FOR_DEVELOPERS_TITLE")}>
					<Html Tag="span">
						{t("FOR_DEVELOPERS_TEXT", {
							contactLink: "/contact",
						})}
					</Html>
				</ConvertFileFormatsHead>
			</ConvertFileFormatsSection>
			<SectionTryForFree />
		</SiteLayout>
	);
};

export default pipe(ConvertFileFormats, WithLocale({ resources }));
